import { ClickAwayListener, Grid, Popper } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { styled } from "@mui/system";
import { debounce } from "lodash";
import React from "react";
import { trackEvent } from "src/Amplitude";
import api from "src/store/features/api";
import Preloader from "src/utils/Preloader";
import useTableSearch from "src/utils/hooks/useTableSearch";
import SearchListGlobal from "../markets/Filter/searchbar/SearchListGlobal";
import { searchConstants } from "../markets/Filter/searchbar/constants";

const SearchbarWrapper = styled("div")(() => ({
  position: "relative",
  width: "100%",
  height: "100%",
  cursor: "pointer",
}));

const IconWrapper = styled("div")(() => ({
  height: "100%",
  width: "30px",
  position: "absolute",
  top: "50%",
  left: "0px",
  transform: "translateY(-50%)",
  color: "#ccc",
  display: "flex",
  alignItems: "center",
  paddingLeft: "10px",
}));

// DO NOT TOUCH
const IconBorderBlockWrapper = styled("div")(() => ({
  position: "relative",
  top: 0,
  left: 0,
  cursor: "pointer",
}));

// DO NOT TOUCH
const IconBorderBlock = styled("div")(() => ({
  position: "absolute",
  top: "0px",
  left: "28px",
  display: "flex",
  width: "10px",
  height: "31px",
  borderTopRightRadius: "10px",
  borderBottomRightRadius: "10px",
  background: "#F8F9F9",
}));

const Input = styled("input")(() => ({
  width: "400px",
  height: "31px",
  display: "inline-flex",
  padding: "8px 12px 8px 35px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "10px",
  background: "#F8F9F9",
  border: "1px solid transparent",
  outline: "none",

  "&::placeholder": {
    color: "#7B858B",
    userSelect: "none",
  },

  // if you want to change the placeholder text when clicked, do so here
  "&:focus::placeholder": {},

  "&:focus": {
    border: "1px solid #E5E7E8",
    background: "#FFF",
  },
}));

export const PopperWrapper = styled(Grid)(({ theme }) => ({
  maxHeight: "480px",
  width: "400px",
  overflowY: "auto",
  borderRadius: "0px 0px 8px 8px",
  height: "auto",
  backgroundColor: theme.palette.background.paper,
  gap: "8px",
  position: "relative",
  zIndex: "9999",
  padding: "4px 8px",
  marginTop: "10px",
  boxShadow: `0px 8px 16px 0px ${
    theme.palette.mode === "light" ? "rgba(216, 216, 218, 0.90)" : "rgba(114, 115, 116, 0.75)"
  }`,
  [theme.breakpoints.down(1250)]: {
    maxWidth: "400px",
  },
}));

interface ErrorProps {
  error: boolean;
}
const PopperError = styled("div")<ErrorProps>(({ error }) => ({
  display: error ? "block" : "none",
  color: "#FC4C4C",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

const DEBOUNCE_TIME = 300;
const CollapsibleGlobalSearchbar = () => {
  const [allCrypto] = api.useLazyGetCryptoCurrenciesQuery();
  const [open, setOpen] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const checked = true;
  // const [checked, setChecked] = React.useState<boolean>(true);

  // Currently, the searchbar is always open
  // so this function is not used
  // const toggleSearchbar = () => {
  //   setChecked((prev) => !prev);
  //   setOpen((prev) => !prev);
  // };

  const closeSearchbar = () => {
    // setChecked(false);
    setOpen(false);
  };

  // enable/disable focus on input.
  // prevent input outline when clicking icon
  React.useEffect(() => {
    if (checked) {
      focusInput();
    } else {
      disableFocusInput();
    }
  }, [checked]);

  const isMounted = React.useRef(false);

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // get / set search value
  const [searchVal, setSearchVal] = React.useState<string | null>(null);

  // filter data with search value
  const { filteredData } = useTableSearch({
    searchVal,
    retrieve: allCrypto,
  });

  // validate the users input
  const validateInput = (e: any) => {
    if (e.target.value === "") {
      setError(false);
      setSearchVal(null);
      return false;
    }

    if (e.target.value.match(/^[a-zA-Z0-9_ \b]+$/)) {
      setError(false);
      return true;
    } else {
      setError(true);
      console.warn("Only alphanumerical characters allowed");
      return false;
    }
  };

  // when user inputs, set search value and validate
  const handleSearchChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    if (validateInput(e)) {
      if (isMounted.current) {
        setSearchVal(e.target.value);
        trackEvent(`Select_Search_Bar`, { token: e.target.value }, "Global_Search_Bar");
      }
      return;
    }
  }, DEBOUNCE_TIME);

  const iconRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  const disableFocusInput = () => {
    if (inputRef.current) {
      inputRef.current.tabIndex = -1;
    }
  };

  return (
    <ClickAwayListener onClickAway={closeSearchbar}>
      <div style={{ paddingRight: "8px" }} ref={iconRef} onClick={() => setOpen(true)}>
        {/* DO NOT TOUCH IconBorderBlockWrapper*/}
        <IconBorderBlockWrapper>
          <IconBorderBlock />
        </IconBorderBlockWrapper>

        {/* Searchbar */}
        <Collapse in={checked} orientation="horizontal" collapsedSize={30}>
          <SearchbarWrapper>
            <IconWrapper>
              <MagnifyingGlass />
            </IconWrapper>
            <Input placeholder="Search" ref={inputRef} onChange={handleSearchChange} />
          </SearchbarWrapper>
        </Collapse>

        {/* Coin Popup */}
        <Popper
          open={open}
          anchorEl={iconRef.current}
          placement={"bottom-start"}
          sx={{ zIndex: 10000 }}>
          <PopperWrapper container>
            <Grid item xs={12}>
              {/* ERROR */}
              <PopperError error={error}>{searchConstants.errorMessage}</PopperError>
              {/* LIST */}
              {!searchVal && filteredData ? (
                <>
                  {filteredData.length <= 0 && <Preloader />}
                  <SearchListGlobal
                    data={filteredData.slice(0, 5)}
                    setOpenClose={() => setOpen(false)}
                  />
                </>
              ) : (
                <SearchListGlobal data={filteredData} setOpenClose={() => setOpen(false)} />
              )}
            </Grid>
          </PopperWrapper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default CollapsibleGlobalSearchbar;

const MagnifyingGlass = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M7.16448 13.329C10.569 13.329 13.329 10.569 13.329 7.16448C13.329 3.75993 10.569 1 7.16448 1C3.75993 1 1 3.75993 1 7.16448C1 10.569 3.75993 13.329 7.16448 13.329Z"
        stroke="#7B858B"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.0001 15.0001L11.5146 11.5146"
        stroke="#7B858B"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
